import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { AlertTriangle, MapPin, CheckCheck } from "lucide-react";
import { toast } from "sonner";

// Manager-facing widget listing unresolved alerts (e.g. geofence exits).
// Auto-refreshes every 60 seconds.
const REFRESH_MS = 60_000;

export default function AlertsWidget() {
  const { t } = useLang();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { setAlerts(await api.get("/alerts?unresolved=true")); }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const resolve = async (id) => {
    try {
      await api.post(`/alerts/${id}/resolve`, {});
      setAlerts((a) => a.filter((x) => x.id !== id));
      toast.success(t("resolved") || "Resolved");
    } catch { toast.error(t("error")); }
  };

  if (loading || alerts.length === 0) return null;

  return (
    <div data-testid="alerts-widget" className="bg-amber-50 border-l-4 border-amber-400 rounded-lg overflow-hidden mb-6">
      <div className="px-4 py-3 flex items-center gap-2 border-b border-amber-200">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-bold text-amber-900">{t("activeAlerts") || "Active Alerts"} ({alerts.length})</span>
      </div>
      <ul className="divide-y divide-amber-200">
        {alerts.slice(0, 5).map((a) => (
          <li key={a.id} data-testid={`alert-${a.id}`} className="px-4 py-2.5 flex items-center gap-3">
            <MapPin className="w-4 h-4 text-amber-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-800 truncate">
                <span className="font-semibold">{a.tech_name}</span>{" "}
                <span className="text-slate-500">
                  {t("wasAway") || "was"} <span className="font-semibold tabular-nums">{a.distance_miles} mi</span>{" "}
                  {t("fromJobSite") || "from job site"} • {t("radiusIs") || "radius"} {a.radius_miles} mi
                </span>
              </div>
              <div className="text-[11px] text-slate-500 truncate">
                {a.ticket_number} — {a.ticket_title} • {a.created_at?.slice(0, 16).replace("T", " ")}
              </div>
            </div>
            <button
              data-testid={`alert-resolve-${a.id}`}
              onClick={() => resolve(a.id)}
              className="inline-flex items-center gap-1 text-xs font-medium bg-white border border-amber-300 hover:bg-amber-100 text-amber-800 rounded-lg px-2.5 py-1.5"
            >
              <CheckCheck className="w-3.5 h-3.5" /> {t("resolve") || "Resolve"}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
